// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import addReorderPadFilters from "@adl/App/Handlers/ReorderPad/AddReorderPadFilters";
import clearAllReorderPadProductFilters from "@adl/App/Handlers/ReorderPad/ClearAllReorderPadProductFilters";
import removeReorderPadFilters from "@adl/App/Handlers/ReorderPad/RemoveReorderPadFilters";
import { CustomAppState } from "@adl/App/Store/Reducers";
import { getReorderPadDataViewProperty } from "@adl/App/Store/Selectors/ReorderPadSelectors";
import { DivWrapper } from "@adl/XDL/constants";
import Search from "@adl/XDL/Icons/Search";
import { color } from "@adl/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {}

const getTags = (key: string, data?: string | string[], label?: string) => {
    if (!data) {
        return [];
    }
    if (typeof data === "string") {
        data = [data];
    }
    return data.map(item => ({ value: item, key, label }));
};
const mapStateToProps = (state: CustomAppState) => {
    const tags = [
        ...getTags("searchWithin", state.xReorderPad.productFilters.searchWithin, "Search"),
        ...getTags("fromDate", state.xReorderPad.productFilters.fromDate, "From"),
        ...getTags("toDate", state.xReorderPad.productFilters.toDate, "To"),
    ];
    return {
        loaded: !!getReorderPadDataViewProperty(state, "items"),
        tags,
    };
};

const mapDispatchToProps = {
    addReorderPadFilters,
    removeReorderPadFilters,
    clearAllReorderPadProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ReorderPadSearchWithinStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    searchTextField?: TextFieldPresentationProps;
    filterTag?: TagPresentationProps;
    clearAllLink?: LinkPresentationProps;
    tags?: {
        wrapper?: InjectableCss;
        titleWrapper?: InjectableCss;
        title?: TypographyPresentationProps;
    };
}

export const searchWithinStyles: ReorderPadSearchWithinStyles = {
    wrapper: {
        css: css`
            padding: 12px;
            background: ${color("common.background")};
            border: 1px solid ${color("common.border")};
            margin-bottom: 12px;
            border-radius: 2px;
            ${DivWrapper}:not(:empty) {
                margin-top: 16px;
            }
        `,
    },
    titleText: {
        variant: "h6",
    },
    searchTextField: {
        iconProps: { src: Search, color: "secondary" },
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
    clearAllLink: {
        typographyProps: {
            size: 12,
            weight: "bold",
        },
    },
    tags: {
        wrapper: {
            css: css`
                display: flex;
                flex-wrap: wrap;
                width: 100%;
                margin-top: 12px;
            `,
        },
        titleWrapper: {
            css: css`
                flex-basis: 100%;
                display: flex;
                align-items: center;
                justify-content: space-between;
                margin-bottom: 8px;
            `,
        },
        title: {
            variant: "h6",
            css: css`
                margin-bottom: 0;
            `,
        },
    },
};

const styles = searchWithinStyles;

const ReorderPadSearchWithin: FC<Props> = ({
    removeReorderPadFilters,
    loaded,
    clearAllReorderPadProductFilters,
    addReorderPadFilters,
    tags,
}) => {
    const [searchText, setSearchText] = useState("");

    if (!loaded) {
        return null;
    }

    const doSearch = () => {
        if (searchText.trim() !== "") {
            addReorderPadFilters({ searchWithin: [searchText] });
        }
        setSearchText("");
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            doSearch();
        } else {
            setSearchText(event.currentTarget.value);
        }
    };

    const clickRemoveHandler = (search: string, key: string) => {
        removeReorderPadFilters({ [key]: [search] });
    };

    const pageType = "Results";
    const label = translate(`Search Within ${pageType}`);

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography forwardAs="label" htmlFor="productsKeywordSearch" {...styles.titleText}>
                {label}
            </Typography>
            <TextField
                {...styles.searchTextField}
                iconClickableProps={{ onClick: doSearch, "aria-roledescription": `Search within ${pageType}` }}
                value={searchText}
                onChange={event => setSearchText(event.currentTarget.value)}
                onKeyDown={handleKeyDown}
                placeholder={translate("Search by Keyword")}
                type="text"
                data-test-selector="ReorderPadSearchWithin"
                id="productsKeywordSearch"
            />
            {tags && tags.length > 0 && (
                <StyledWrapper {...styles.tags?.wrapper}>
                    {tags?.map(s => (
                        <Tag key={s.value} {...styles.filterTag} onDelete={() => clickRemoveHandler(s.value, s.key)}>
                            {s.value}
                        </Tag>
                    ))}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ReorderPadSearchWithin);
